import React, { useState } from 'react';
import { Table, Button, Space, message, Modal, Form, Input } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';

const { TextArea } = Input;

const PrivacyPolicy = () => {
    const [policies, setPolicies] = useState([
        {
            id: 1,
            title: 'Data Collection Policy',
            content: 'We collect information that you provide directly to us...',
            updatedAt: '2025-01-15',
        },
        {
            id: 2,
            title: 'Data Usage Policy',
            content: 'We use the information we collect to provide, maintain...',
            updatedAt: '2025-01-10',
        },
    ]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingPolicy, setEditingPolicy] = useState(null);
    const [form] = Form.useForm();

    const handleAddPolicy = () => {
        setEditingPolicy(null);
        setIsModalVisible(true);
        form.resetFields();
    };

    const handleEditPolicy = (record) => {
        setEditingPolicy(record);
        setIsModalVisible(true);
        form.setFieldsValue({
            title: record.title,
            content: record.content,
        });
    };

    const handleDeletePolicy = (id) => {
        setPolicies(policies.filter(policy => policy.id !== id));
        message.success('Policy deleted successfully');
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setEditingPolicy(null);
        form.resetFields();
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();

            if (editingPolicy) {
                // Update existing policy
                setPolicies(policies.map(policy =>
                    policy.id === editingPolicy.id
                        ? {
                            ...policy,
                            title: values.title,
                            content: values.content,
                            updatedAt: new Date().toISOString().split('T')[0],
                        }
                        : policy
                ));
                message.success('Policy updated successfully');
            } else {
                // Add new policy
                const newPolicy = {
                    id: policies.length > 0 ? Math.max(...policies.map(p => p.id)) + 1 : 1,
                    title: values.title,
                    content: values.content,
                    updatedAt: new Date().toISOString().split('T')[0],
                };
                setPolicies([...policies, newPolicy]);
                message.success('Policy added successfully');
            }

            setIsModalVisible(false);
            setEditingPolicy(null);
            form.resetFields();
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Content',
            dataIndex: 'content',
            key: 'content',
            ellipsis: true,
            render: (text) => text.length > 100 ? `${text.substring(0, 100)}...` : text,
        },
        {
            title: 'Last Updated',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 150,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEditPolicy(record)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this policy?"
                        onConfirm={() => handleDeletePolicy(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            danger
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1>Privacy Policy</h1>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddPolicy}
                >
                    Add New Policy
                </Button>
            </div>
            <Table
                columns={columns}
                dataSource={policies}
                rowKey="id"
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} policies`,
                }}
            />

            <Modal
                title={editingPolicy ? "Edit Policy" : "Add New Policy"}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                okText={editingPolicy ? "Update Policy" : "Add Policy"}
                cancelText="Cancel"
                width={700}
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        label="Title"
                        name="title"
                        rules={[
                            {
                                required: true,
                                message: 'Please enter policy title!',
                            },
                        ]}
                    >
                        <Input placeholder="Enter policy title" />
                    </Form.Item>

                    <Form.Item
                        label="Content"
                        name="content"
                        rules={[
                            {
                                required: true,
                                message: 'Please enter policy content!',
                            },
                        ]}
                    >
                        <TextArea
                            rows={8}
                            placeholder="Enter policy content"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PrivacyPolicy;

